import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {debounce} from "../../utils/convert";
import {wikiAPI} from "../../api/wp";
import IOption from "./../../types/option";
import useOutsideAlerter from "../../hooks/UseOutsideAlerter";
import {checkWikiUrl} from "../../utils/parsing";
import classNames from "classnames";
import PlusIcon from "./icons/PlusIcon";
import {useSearchParams} from "react-router-dom";

type TSearchProps = {
    onChoose: (option: IOption) => void
    chooseButton?: boolean
}

const Search: FC<TSearchProps> = ({onChoose, chooseButton = false}) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const isSearchMode = searchParams.get('mode') === 'search'
    const inputRef = useRef(null)
    const wrapperRef = useRef(null)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [inputText, setInputText] = useState<string>('')
    const [options, setOptions] = useState<IOption[]>([])

    const [searchAutoComplete, {data: autoCompleteData, isLoading, isSuccess: searchAutoCompleteSuccess}] = wikiAPI.useSearchAutoCompleteMutation()
    const [checkPageExists, {data: checkPageData, isLoading: checkPageExistsLoading, isSuccess: checkPageExistsSuccess}] = wikiAPI.useCheckPageExistsMutation()

    const debouncedSearchAutoComplete = useMemo(() => {
        return debounce((value: string) => {
            if (value.length < 1) {
                setOptions([])
                return;
            }

            searchAutoComplete(value)
        }, 1000)
    },[])

    const debouncedCheckPageExists = useMemo(() => {
        return debounce((value: string) => {
            if (value.length < 2) {
                setOptions([])
                return;
            }

            checkPageExists(value)
        }, 1000)
    },[])

    const chooseOption = async (option: IOption) => {
        setIsFocused(false)
        setInputText(option.title)

        onChoose(option)
    }

    useOutsideAlerter(wrapperRef, () => setIsFocused(false))

    const focusInput = () => {
        setIsFocused(true)
    }

    const changeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputText(value ?? '')

        const wikiPageSlug = checkWikiUrl(value)

        if (wikiPageSlug) {
            debouncedCheckPageExists(wikiPageSlug)
            return;
        }

        debouncedSearchAutoComplete(value)
    }

    useEffect(() => {
        setOptions(checkPageData ?? [])
    }, [checkPageData])

    useEffect(() => {
        setOptions(autoCompleteData ?? [])
    }, [autoCompleteData])

    return (
        <section className="mb-[15px]">
            <div className={classNames('', {
                'container': !isSearchMode
            })}>
                <div className="relative min-h-[40px]">
                    <div className={classNames('absolute z-[100] w-full border-[2px] rounded-lg', {
                        'border-red-400': inputText.length >= 2 && (autoCompleteData && !autoCompleteData.length && searchAutoCompleteSuccess)
                            || (checkPageData && !checkPageData.length && checkPageExistsSuccess)
                    })}>
                        <div className="text-sm text-[#111111] w-full z-10 rounded-lg max-h-[300px] overflow-y-scroll" ref={wrapperRef}>
                            <input
                                className={classNames('block w-full px-3 py-2 rounded-lg  focus:outline-none placeholder-gray-700', {})}
                                type="text"
                                placeholder="Search..."
                                autoComplete="off"
                                value={inputText}
                                onChange={changeInput}
                                onFocus={focusInput}
                                ref={inputRef}
                            />
                            {isFocused && options && options.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={(e) => !chooseButton && chooseOption(option)}
                                    className={classNames('block px-3 py-2 flex text-sm text-[#111111] bg-[#EFEFEF] hover:bg-[#DFDFDF]', {
                                        'cursor-pointer': !chooseButton
                                    })}
                                >
                                    <div className="w-[35px] h-[35px] mr-2 flex items-center overflow-hidden">
                                        {option.img ? <img
                                            src={option.img}
                                            alt={option.title}
                                            className="max-w-full h-auto w-full"
                                        /> : <div className="w-full h-full bg-gray-200" />}
                                    </div>

                                    <span className="mr-3 flex items-center">{option.title}</span>
                                    {!!chooseButton && <span
                                        onClick={(e) => chooseOption(option)}
                                        className="cursor-pointer flex items-center"
                                    >
                                        <PlusIcon />
                                    </span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Search;